import React from "react";
import { COLOR_SYSTEM } from "../../design/design-system";
import { Patient, PrescriptionGroup, AIReview } from "../../../common/models/types";
import { calculateAge } from "../../../utils/TimeUtil";

export interface ReportPrintViewProps {
  patient: Patient;
  review: AIReview;
  consultationNotes?: string;
}

const ReportPrintView: React.FC<ReportPrintViewProps> = ({
  patient,
  review,
  consultationNotes,
}) => {
  // 환자 나이 계산
  const age = patient.birthDate ? calculateAge(patient.birthDate) : "미상";

  // 처방전 그룹별로 정리
  const prescriptionGroups = patient.prescriptionGroups || [];

  // 리뷰 결과를 HTML로 파싱하여 처리
  const formatReviewContent = (content: string) => {
    if (!content) return "";

    // HTML 태그를 그대로 유지하면서 특정 패턴만 변환
    let formattedContent = content;

    // 참고문헌 변환 (다른 변환보다 먼저 실행)
    // 참고문헌 ID와 번호 매핑을 위한 Map
    const referenceMap = new Map<string, number>();
    let referenceCounter = 1;
    
    // 모든 참고문헌 ID를 먼저 찾아서 번호 할당
    const allReferences = [
      ...Array.from(formattedContent.matchAll(/#([A-Za-z0-9]+)/g)),
      ...Array.from(formattedContent.matchAll(/⑆([A-Za-z0-9_]+)/g))
    ];
    
    console.log('ReportPrintView - 찾은 참고문헌:', allReferences.length, allReferences);
    
    // 순서대로 번호 할당 (중복 ID는 같은 번호 사용)
    allReferences.forEach(match => {
      const refId = match[1];
      if (!referenceMap.has(refId)) {
        referenceMap.set(refId, referenceCounter++);
      }
    });
    
    console.log('ReportPrintView - 참고문헌 매핑:', Object.fromEntries(referenceMap));
    
    // 기존 #참고문헌ID 패턴을 파란색 링크로 변환
    formattedContent = formattedContent.replace(
      /#([A-Za-z0-9]+)/g,
      (match, refId) => {
        const number = referenceMap.get(refId) || 1;
        console.log(`ReportPrintView - #변환: ${match} → ref${number}`);
        return `<span style="color: #007bff; text-decoration: underline; font-weight: 500; font-size: 12px; padding: 0 2px;">ref${number}</span>`;
      }
    );

    // ⑆상담필요 패턴을 주황색으로 변환 (참고문헌 변환보다 먼저 실행)
    formattedContent = formattedContent.replace(
      /⑆상담필요/g,
      '<span style="color: #ff8c00; text-decoration: underline; font-weight: 500; font-size: 12px; padding: 0 2px;">상담필요</span>'
    );

    // 새로운 ⑆참고문헌ID 패턴을 파란색 링크로 변환
    formattedContent = formattedContent.replace(
      /⑆([A-Za-z0-9_]+)/g,
      (match, refId) => {
        const number = referenceMap.get(refId) || 1;
        console.log(`ReportPrintView - ⑆변환: ${match} → ref${number}`);
        return `<span style="color: #007bff; text-decoration: underline; font-weight: 500; font-size: 12px; padding: 0 2px;">ref${number}</span>`;
      }
    );

    // 마크다운 굵은 글씨 변환 (참고문헌 변환 후, 헤더 변환 전)
    formattedContent = formattedContent.replace(
      /\*\*(.*?)\*\*/g,
      '<strong style="font-weight: bold;">$1</strong>'
    );

    // 불릿 리스트 변환 (굵은 글씨 변환 후, 헤더 변환 전)
    // 연속된 "- " 항목들을 찾아서 <ul>로 감싸기
    formattedContent = formattedContent.replace(
      /((?:^- .*$\n?)+)/gm,
      (match) => {
        const items = match.trim().split('\n').map(item => 
          `<li style="margin: 5px 0;">${item.substring(2)}</li>`
        ).join('');
        return `<ul style="margin: 10px 0; padding-left: 30px; list-style-type: disc;">${items}</ul>`;
      }
    );

    // 마크다운 헤더를 HTML로 변환 (참고문헌 변환 후 실행)
    // ### 제목 (3개 #)
    formattedContent = formattedContent.replace(
      /^### ([^#].*?)(\n|$)/gm,
      '<h5 style="background-color: #b48ead; color: black; padding: 5px; margin: 20px 0 10px 0;">$1</h5>'
    );
    // ## 제목 (2개 #)
    formattedContent = formattedContent.replace(
      /^## ([^#].*?)(\n|$)/gm,
      '<h4 style="background-color: #a3be8c; color: black; padding: 5px; margin: 20px 0 10px 0;">$1</h4>'
    );
    // # 제목 (1개 #, 하지만 참고문헌이 아닌 경우만)
    formattedContent = formattedContent.replace(
      /^# ([^#].*?)(\n|$)/gm,
      '<h3 style="background-color: #ebcb8b; color: black; padding: 5px; margin: 20px 0 10px 0;">$1</h3>'
    );

    // 강조 텍스트 처리
    formattedContent = formattedContent.replace(
      /\$(.*?)\$/g,
      '<span style="color: red; font-weight: bold;">$1</span>'
    );

    // 줄바꿈 처리 (마지막에 실행)
    // 연속된 줄바꿈은 하나로 처리하고, 단일 줄바꿈만 <br>로 변환
    formattedContent = formattedContent.replace(/\n{3,}/g, "\n\n"); // 3개 이상의 연속 줄바꿈을 2개로
    formattedContent = formattedContent.replace(/\n\n/g, "<br><br>"); // 빈 줄은 유지
    formattedContent = formattedContent.replace(/\n/g, "<br>"); // 일반 줄바꿈

    return formattedContent;
  };

  // 생년월일을 문자열로 변환
  const formatBirthDate = (birthDate?: Date | string) => {
    if (!birthDate) return "미기재";
    if (typeof birthDate === "string") return birthDate;
    return birthDate.toLocaleDateString();
  };

  // 스타일 객체들
  const printContainerStyle: React.CSSProperties = {
    fontFamily: '"Malgun Gothic", Arial, sans-serif',
    lineHeight: "1.6",
    margin: "0 auto",
    padding: "20px",
    color: "#333",
    backgroundColor: "white",
    maxWidth: "1000px",
  };

  const patientHeaderStyle: React.CSSProperties = {
    backgroundColor: "black",
    color: "white",
    padding: "10px",
    margin: "0 0 20px 0",
    fontSize: "24px",
    fontWeight: "bold",
  };

  const sectionTitleStyle: React.CSSProperties = {
    color: "#333",
    fontSize: "20px",
    margin: "20px 0 10px 0",
    borderBottom: "2px solid #333",
    paddingBottom: "5px",
  };

  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "1rem",
  };

  const thStyle: React.CSSProperties = {
    padding: "8px",
    textAlign: "left",
    border: "1px solid #dee2e6",
    verticalAlign: "top",
    backgroundColor: "#f8f9fa",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  };

  const tdStyle: React.CSSProperties = {
    padding: "8px",
    textAlign: "left",
    border: "1px solid #dee2e6",
    verticalAlign: "top",
  };

  const codeStyle: React.CSSProperties = {
    color: "blue",
    textDecoration: "underline",
    fontSize: "12px",
  };

  return (
    <div style={printContainerStyle}>
      <h1 style={patientHeaderStyle}>[환자{patient.id}] 환자 정보</h1>

      <div style={{ marginBottom: "20px", borderBottom: "1px solid #ccc", paddingBottom: "15px" }}>
        <h2 style={sectionTitleStyle}>Patient Info</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px",
            marginBottom: "15px",
          }}
        >
          <div>
            <strong>질병명:</strong> {patient.diseases || "미기재"}
          </div>
          <div>
            <strong>생년월일:</strong> {formatBirthDate(patient.birthDate)}
          </div>
          <div>
            <strong>임신여부:</strong> {patient.pregnancyStatus ? "임신" : "해당없음"}
          </div>
          <div>
            <strong>기저질환:</strong> {patient.allergies || "미기재"}
          </div>
          <div>
            <strong>나이:</strong> {age}
          </div>
          <div>
            <strong>성별:</strong> {patient.gender || "미기재"}
          </div>
        </div>
        <div>
          <strong>추가 정보:</strong> {patient.consultationNotes || "추가정보 없음"}
        </div>
      </div>

      <div style={{ marginBottom: "30px" }}>
        <h2 style={sectionTitleStyle}>Prescriptions</h2>
        {prescriptionGroups.map((group, index) => (
          <div key={group.groupId || index} style={{ marginBottom: "20px" }}>
            <h4>처방전{index + 1}</h4>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>의약품 명칭</th>
                  <th style={thStyle}>투약량</th>
                  <th style={thStyle}>횟수</th>
                  <th style={thStyle}>일수</th>
                  <th style={thStyle}>용법</th>
                </tr>
              </thead>
              <tbody>
                {group.prescriptions?.map((prescription, pIndex) => (
                  <tr key={prescription.id || pIndex}>
                    <td style={tdStyle}>{prescription.medicationName || "-"}</td>
                    <td style={tdStyle}>{prescription.dosagePerAdministration || "-"}</td>
                    <td style={tdStyle}>{prescription.timesPerDay || "-"}</td>
                    <td style={tdStyle}>{prescription.daysOfAdministration || "-"}</td>
                    <td style={tdStyle}>{prescription.usageInstruction || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div style={{ marginTop: "30px" }}>
        <h2 style={sectionTitleStyle}>검토 결과</h2>
        <div dangerouslySetInnerHTML={{ __html: formatReviewContent(review.reviewResult || "") }} />
      </div>
    </div>
  );
};

export default ReportPrintView;
